import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AttributeListState } from '../state/attribute-list.state';
import {
  selectAttributeListPanelTitle, selectAttributeListSelectedTab, selectAttributeListTabs,
  selectAttributeListVisible,
  selectCurrentlySelectedFeatureGeometry,
} from '../state/attribute-list.selectors';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { setAttributeListVisibility, setSelectedTab } from '../state/attribute-list.actions';
import { MatTabChangeEvent, MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { AttributeListTabModel } from '../models/attribute-list-tab.model';
import { MenubarService } from '../../menubar';
import { AttributeListMenuButtonComponent } from '../attribute-list-menu-button/attribute-list-menu-button.component';
import { FeatureStylingHelper } from '../../../shared/helpers/feature-styling.helper';
import { MapService } from '@tailormap-viewer/map';
import { BaseComponentTypeEnum } from '@tailormap-viewer/api';
import { AttributeListManagerService } from '../services/attribute-list-manager.service';
import { OverlayRef, PopoverPositionEnum, PopoverService, TooltipDirective } from '@tailormap-viewer/shared';
import { AttributeListColumnSelectionComponent } from '../attribute-list-column-selection/attribute-list-column-selection.component';
import { BottomPanelComponent } from '../../../shared/components/bottom-panel/bottom-panel.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AttributeListTabComponent } from '../attribute-list-tab/attribute-list-tab.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-attribute-list',
    templateUrl: './attribute-list.component.html',
    styleUrls: ['./attribute-list.component.css'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BottomPanelComponent,
        MatProgressSpinner,
        MatTabGroup,
        MatTab,
        MatTabLabel,
        MatIconButton,
        TooltipDirective,
        MatIcon,
        AttributeListTabComponent,
        AsyncPipe,
    ],
})
export class AttributeListComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<AttributeListState>>(Store);
  private menubarService = inject(MenubarService);
  private mapService = inject(MapService);
  private popoverService = inject(PopoverService);
  public isLoadingTabs$ = inject(AttributeListManagerService).isLoadingTabs$();

  public isVisible$: Observable<boolean>;

  public tabs = this.store$.selectSignal(selectAttributeListTabs);
  private destroyed = new Subject();

  public selectedTab = this.store$.selectSignal(selectAttributeListSelectedTab);
  public title$: Observable<string> = of('');

  private columnSelectionOverlayRef: OverlayRef | undefined;

  constructor() {
    this.isVisible$ = this.store$.select(selectAttributeListVisible);
    this.title$ = this.store$.select(selectAttributeListPanelTitle);
  }

  public ngOnInit() {
    this.menubarService.registerComponent({ type: BaseComponentTypeEnum.ATTRIBUTE_LIST, component: AttributeListMenuButtonComponent });

    this.mapService.renderFeatures$(
      'attribute-list-highlight-layer',
      this.store$.select(selectCurrentlySelectedFeatureGeometry),
      FeatureStylingHelper.getDefaultHighlightStyle('attribute-list-highlight-style'),
      { zoomToFeature: true },
    )
      .pipe(takeUntil(this.destroyed))
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.menubarService.deregisterComponent(BaseComponentTypeEnum.ATTRIBUTE_LIST);
    this.store$.dispatch(setAttributeListVisibility({ visible: false }));
    if (this.columnSelectionOverlayRef) {
      this.columnSelectionOverlayRef.close();
    }
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public onCloseClick(): void {
    this.store$.dispatch(setAttributeListVisibility({ visible: false }));
  }

  public onSelectedTabChange($event: MatTabChangeEvent): void {
    this.store$.dispatch(setSelectedTab({ tabId: this.tabs()[$event.index].id }));
  }

  public trackByTabId(idx: number, layer: AttributeListTabModel) {
    return layer.id;
  }

  public openColumnSelection($event: MouseEvent, tab: AttributeListTabModel) {
    $event.preventDefault();
    $event.stopPropagation();
    if (this.columnSelectionOverlayRef && this.columnSelectionOverlayRef.isOpen) {
      this.columnSelectionOverlayRef.close();
      return;
    }
    const WINDOW_WIDTH = 300;
    this.columnSelectionOverlayRef = this.popoverService.open({
      origin: $event.currentTarget as HTMLElement,
      content: AttributeListColumnSelectionComponent,
      data: { dataId: tab.selectedDataId },
      height: 250,
      width: Math.min(WINDOW_WIDTH, window.innerWidth),
      closeOnClickOutside: true,
      position: PopoverPositionEnum.BOTTOM_RIGHT_DOWN,
      positionOffset: 10,
    });
  }

}
